/**
 * Thin selector that merges the Spreken study topics and the Schrijven study
 * topics into one list for the consolidated /grammatica destination. No new
 * topic content is authored here — this is purely a merge + lookup layer over
 * the two existing registries (DRY).
 */
import { isDutchPhrase } from "@a2/i18n/helpers";
import {
  listSchrijvenTopics,
  getSchrijvenTopicBySlug,
} from "../schrijven-study/topics";
import type { SchrijvenTopic } from "../schrijven-study/topics/types";
import { getTopicBySlug, listTopics } from "./topics";
import type { Topic, TopicSection, TopicText } from "./topics/types";

export type ExamFacet = "spreken" | "schrijven";

export type GrammaticaEntry = {
  slug: string;
  title: string;
  tagline: string;
  duration: string;
  summary: string;
  exam: ExamFacet;
  /** The deelen this chapter helps with (spreken only); empty for schrijven. */
  relevantDeelen: readonly number[];
};

export type GrammaticaTopic =
  | { topic: Topic; exam: "spreken" }
  | { topic: SchrijvenTopic; exam: "schrijven" };

function toEntry(t: Topic | SchrijvenTopic, exam: ExamFacet): GrammaticaEntry {
  return {
    slug: t.slug,
    title: t.title,
    tagline: t.tagline,
    duration: t.duration,
    summary: t.summary,
    exam,
    relevantDeelen: "relevantDeelen" in t ? t.relevantDeelen : [],
  };
}

/** Spreken entries first (registry order), then schrijven. */
export function listGrammatica(): GrammaticaEntry[] {
  return [
    ...listTopics().map((t) => toEntry(t, "spreken")),
    ...listSchrijvenTopics().map((t) => toEntry(t, "schrijven")),
  ];
}

/** Try the spreken registry first, then schrijven. */
export function getGrammaticaTopic(slug: string): GrammaticaTopic | undefined {
  const spreken = getTopicBySlug(slug);
  if (spreken) return { topic: spreken, exam: "spreken" };
  const schrijven = getSchrijvenTopicBySlug(slug);
  if (schrijven) return { topic: schrijven, exam: "schrijven" };
  return undefined;
}

// ---------------------------------------------------------------------------
// Shared text helpers — used by the selector (TOC) and TopicRenderer (ids) so
// both produce identical heading anchors.
// ---------------------------------------------------------------------------

/** Flatten a TopicText (string | inline pieces) to a plain string. */
export function flattenTopicText(text: TopicText): string {
  if (typeof text === "string") return text;
  return text.map((p) => (isDutchPhrase(p) ? p.text : p)).join("");
}

/** Slugified, stable id for a heading section so the TOC can anchor to it. */
export function topicHeadingId(text: TopicText): string {
  return (
    flattenTopicText(text)
      .toLowerCase()
      .normalize("NFKD")
      // biome-ignore lint/suspicious/noMisleadingCharacterClass: combining-diacritic range strip
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "sectie"
  );
}

export type TocHeading = { id: string; text: string };

/** Derive level-2/3 headings from a topic's sections for the reader TOC. */
export function tocHeadingsFor(sections: readonly TopicSection[]): TocHeading[] {
  return sections
    .filter((s): s is Extract<TopicSection, { kind: "heading" }> => s.kind === "heading")
    .map((s) => ({ id: topicHeadingId(s.text), text: flattenTopicText(s.text) }));
}
